import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const languages = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "pl", label: "Polski" },
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function changeLanguage(code: string) {
    i18n.changeLanguage(code);
    setOpen(false);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="whitespace-nowrap flex items-center gap-2 rounded-full border border-background-300/70 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 cursor-pointer transition-colors hover:bg-background-100"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="w-5 h-5 flex items-center justify-center">
          <i className="ri-global-line text-base text-foreground-600" />
        </span>
        {current.label}
        <span className="w-4 h-4 flex items-center justify-center">
          <i className={`ri-arrow-down-s-line text-sm text-foreground-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <ul
          className="absolute right-0 top-full z-50 mt-2 w-44 overflow-hidden rounded-lg border border-background-300/70 bg-background-50 p-1"
          role="listbox"
        >
          {languages.map((lang) => (
            <li key={lang.code}>
              <button
                type="button"
                role="option"
                aria-selected={lang.code === current.code}
                onClick={() => changeLanguage(lang.code)}
                className={`whitespace-nowrap flex w-full items-center justify-between rounded-md px-3 py-2 text-sm cursor-pointer transition-colors ${
                  lang.code === current.code
                    ? "bg-primary-100 text-primary-700 font-semibold"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {lang.label}
                {lang.code === current.code && (
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-check-line text-sm" />
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}